import { useTranslation } from 'react-i18next';

import useUrlParams from '@/utils/useUrlParams';
import { Button, Form, Input, Select } from '@arco-design/web-react';
import { IconSearch } from '@arco-design/web-react/icon';
import { useCallback, useEffect } from 'react';
import styles from './index.module.less';
import i18n from '@/i18n';

const FormItem = Form.Item;
const Option = Select.Option;
/**
 * 搜索组件，根据 schema 描述生成搜索表单，并处理表单的搜索、重置和刷新操作
 * @param {function} getData - 获取数据的函数
 * @param {Array} schema - 表单字段的配置
 * @param {ReactNode} extra - 额外的组件或内容，例如右边的排排对齐的按钮等
 * @param {Array} hiddenBtn - 隐藏的按钮列表，全额传入为['search','refresh','reset']，默认为[]
 * @param {string} queryName - URL 参数的名称，默认为 'query'
 * @returns {JSX.Element} - 渲染的搜索组件
 */
export default function Search({
  getData,
  schema,
  extra = null,
  hiddenBtn = ['reset'],
  queryName = 'query',
  style = null,
  resetFlag = false,
  refresh = () => {},
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { setParam, getParam } = useUrlParams();

  useEffect(() => {
    if (resetFlag) {
      form.resetFields();
      setParam(queryName, '{}');
    }
  }, [resetFlag]);

  const handleFormSubmit = (v) => {
    const query = JSON.parse(getParam(queryName, '{}'));
    const vals = {};
    Object.entries({ ...query, ...v }).forEach(([k, t]) => {
      if (t) {
        vals[k] = t;
      }
    });
    getData(v);
    setParam(queryName, JSON.stringify(vals));
  };

  const handleReFresh = () => {
    form.submit();
    refresh();
  };

  const handleReset = () => {
    form.resetFields();
    setParam(queryName, '{}');
    getData({});
  };

  const renderFormItem = useCallback(
    (item) => {
      const {
        field,
        type,
        allowClear = true,
        placeholder,
        label,
        style,
        showSearch,
        unShowItem,
        defaultValue = '',
      } = item;

      if (unShowItem === true) {
        return null;
      }

      // 根据字段类型渲染相应的表单控件
      if (type === 'select') {
        const options = item.options || [];
        return (
          <FormItem
            className={styles.formItem}
            label={label}
            field={field}
            key={field}
            style={{ maxWidth: style?.width || 200, minWidth: style?.minWidth || 150 }}
          >
            <Select
              onChange={() => form.submit()}
              showSearch={showSearch}
              placeholder={placeholder}
              allowClear={allowClear}
              defaultValue={defaultValue}
            >
              {options.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </FormItem>
        );
      }
      form.setFieldsValue({
        porject: 'all',
      });
      // 默认input框
      return (
        <FormItem
          className={styles.formItem}
          key={field}
          field={field}
          style={{ maxWidth: style?.width || 200, minWidth: style?.minWidth || 150 }}
        >
          <Input prefix={<IconSearch />} placeholder={placeholder} allowClear={allowClear} />
        </FormItem>
      );
    },
    [form],
  );

  const renderForm = (
    <Form
      onSubmit={handleFormSubmit}
      className={styles.form}
      style={extra ? {} : { ...(style || {}) }}
      form={form}
      layout="inline"
      initialValues={JSON.parse(getParam(queryName, '{}'))}
    >
      {schema?.length > 0 && schema.map(renderFormItem)}
      {hiddenBtn?.indexOf('search') === -1 && (
        <Button type="primary" htmlType="submit" className="btnStyle">
          {t('common:search')}
        </Button>
      )}
      {hiddenBtn?.indexOf('refresh') === -1 && (
        <Button onClick={handleReFresh}>{t('common:refresh')}</Button>
      )}
      {hiddenBtn?.indexOf('reset') === -1 && (
        <Button onClick={handleReset}>{t('common:reset')}</Button>
      )}
    </Form>
  );

  if ((!schema || !schema?.length) && !extra) return null;

  return (
    <div className={styles.searchContainer} style={style}>
      {schema && schema?.length > 0 && renderForm}
      {/* 额外内容 */}
      {extra}
    </div>
  );
}
